/**
 * Editor «Prato do dia» (#209, spec §8.4): interruptor + «Quando» (só hoje /
 * toda [dias] / de-até). Sem preço especial — preço diferente = promoção
 * (decisão #2). Máx. 2 ativos por janela (a API devolve 422 e o painel
 * mostra o aviso).
 */
import { useState } from 'react'
import { useMutation, type useQueryClient } from '@tanstack/react-query'
import { products as prodApi, type Product } from '../lib/api.js'
import { Button, Switch, toast } from './ui/index.js'
import { cn } from '../lib/utils'
import { useI18n } from '../lib/i18n.js'
import { dayLabels, todayLocal } from '../lib/daily-specials.js'

export function DailySpecialEditor({
  product,
  countryCode,
  qc,
  onProductUpdate,
}: {
  product: Product
  /** #428: país do tenant — início da semana (pt+PT → segunda, pt+BR → domingo) */
  countryCode?: string | null
  qc: ReturnType<typeof useQueryClient>
  onProductUpdate: (p: Product) => void
}) {
  const { t, lang } = useI18n()
  // rótulos dos dias no idioma do painel, ordem pelo PAÍS do restaurante (#428)
  const dayLabelsList = dayLabels(lang, countryCode)
  const special = product.daily_special ?? null
  const [active, setActive] = useState(special?.is_active ?? false)
  const [mode, setMode] = useState<'today' | 'weekly' | 'range'>(special?.mode ?? 'today')
  const [date, setDate] = useState(special?.date ?? todayLocal())
  const [days, setDays] = useState<number>(special?.days_of_week ?? 0)
  const [start, setStart] = useState(special?.start_date ?? todayLocal())
  const [end, setEnd] = useState(special?.end_date ?? todayLocal())

  // produto trocado: recomeça da janela do servidor
  const stamp = `${product.id}|${special?.updated_at ?? ''}`
  const [prevStamp, setPrevStamp] = useState(stamp)
  if (stamp !== prevStamp) {
    setPrevStamp(stamp)
    setActive(special?.is_active ?? false)
    setMode(special?.mode ?? 'today')
    setDate(special?.date ?? todayLocal())
    setDays(special?.days_of_week ?? 0)
    setStart(special?.start_date ?? todayLocal())
    setEnd(special?.end_date ?? todayLocal())
  }

  const save = useMutation({
    mutationFn: () =>
      prodApi.setDailySpecial(product.id, {
        active,
        ...(active
          ? mode === 'today'
            ? { mode, date }
            : mode === 'weekly'
              ? { mode, days_of_week: days || 127 }
              : { mode, start_date: start, end_date: end }
          : {}),
      }),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['products'] })
      toast.success(t('admin.menu.special_saved'))
      if (data) onProductUpdate(data)
    },
    // 422 SPECIAL_LIMIT: o aviso do limite (máx. 2 por janela) do servidor
    onError: (e: Error) => toast.error(e.message),
  })

  const dayLabel = (bit: number, short: string, long: string) => (
    <button
      key={bit}
      type="button"
      data-testid={`catalog-special-day-${bit}`}
      aria-pressed={(days & bit) !== 0}
      aria-label={long}
      title={long}
      onClick={() => setDays((d) => d ^ bit)}
      className={cn(
        // largura mínima fixa e conteúdo centrado (#403 QA): em ar o `short`
        // vem com o nome inteiro, em ja/zh/ko com 1 caractere — o flex-wrap
        // deixa quebrar em 375px sem sobrepor os botões
        'inline-flex min-w-11 items-center justify-center rounded-full border px-1 py-1.5 text-xs transition-colors',
        (days & bit) !== 0
          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
          : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300',
      )}
    >
      {short}
    </button>
  )

  return (
    <div
      className="rounded-xl border border-gray-200 p-4"
      data-testid={`catalog-special-${product.id}`}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <p className="text-sm font-medium text-gray-700">{t('admin.menu.special')}</p>
        <Switch
          checked={active}
          onChange={setActive}
          testId={`catalog-special-toggle-${product.id}`}
          label={t('admin.menu.special_active')}
        />
      </div>
      <p className="text-xs text-gray-400 mb-3">{t('admin.menu.special_hint')}</p>

      {active && (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2" data-testid={`catalog-special-mode-${product.id}`}>
            {(
              [
                ['today', t('admin.menu.special_today')],
                ['weekly', t('admin.menu.special_weekly')],
                ['range', t('admin.menu.special_range')],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                data-testid={`catalog-special-mode-${value}`}
                aria-pressed={mode === value}
                onClick={() => setMode(value)}
                className={cn(
                  'rounded-full border px-3 py-1 text-xs transition-colors',
                  mode === value
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300',
                )}
              >
                {label}
              </button>
            ))}
          </div>

          {mode === 'today' && (
            <label className="flex items-center gap-2 text-xs text-gray-600">
              {t('admin.menu.special_today')}
              <input
                type="date"
                data-testid={`catalog-special-date-${product.id}`}
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="rounded-md border border-gray-200 px-2 py-1"
              />
            </label>
          )}
          {mode === 'weekly' && (
            <div
              className="flex flex-wrap gap-1.5"
              data-testid={`catalog-special-days-${product.id}`}
            >
              {dayLabelsList.map(({ bit, short, long }) => dayLabel(bit, short, long))}
            </div>
          )}
          {mode === 'range' && (
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <input
                type="date"
                data-testid={`catalog-special-start-${product.id}`}
                value={start}
                onChange={(e) => setStart(e.target.value)}
                className="rounded-md border border-gray-200 px-2 py-1"
              />
              →
              <input
                type="date"
                data-testid={`catalog-special-end-${product.id}`}
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                className="rounded-md border border-gray-200 px-2 py-1"
              />
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-end mt-3">
        <Button
          size="sm"
          data-testid={`catalog-special-save-${product.id}`}
          loading={save.isPending}
          disabled={save.isPending}
          onClick={() => save.mutate()}
        >
          {t('admin.common.save')}
        </Button>
      </div>
    </div>
  )
}
